import { useCallback, useMemo, useState, useRef, useEffect, lazy } from 'react';
import { cn } from '@divlab/divanui';

import * as PageProductStore from '@Stores/PageProduct';
import * as HeaderStore from '@Stores/Header';
import * as ModalWindows from '@Stores/ModalWindows';
import * as Meta from '@Queries/Meta';
import { useFeatureFlags } from '@Contexts/FeatureFlags';
import Suspense from '@Components/Suspense';
import ErrorBoundary from '@Components/ErrorBoundary';
import NanoProductCard from '@Components/NanoProductCard';
import InstagramSection from '@Components/InstagramSection';
import LazyProductModel from '@Components/ProductModel/LazyProductModel';
import BreadcrumbsWrapper from '@Components/BreadcrumbsWrapper';
import useMedias from '@Hooks/useMedias';
import useRenderType from '@Hooks/useRenderType';
import useSearchParams from '@Hooks/useSearchParams';
import useTranslation from '@Queries/useTranslation';
import Layout from '@Components/Layout';
import ProductsSection from '@Components/ProductsSection';
import ReviewsSection from '@Pages/PageProduct/elements/ReviewsSection';
import ListReviews from '@Pages/PageProduct/elements/ListReviews';
import EmptyReview from '@Pages/PageProduct/elements/ReviewsSection/elements/EmptyReview';
import Sidebar from './elements/Sidebar';
import ComfortBuy from './elements/ComfortBuy';
import Characteristics from './elements/Characteristics';
import PhotoGallery from './elements/PhotoGallery';
import MattressesLayers from './elements/MattressesLayers';
import ChooseMattressBanner from './elements/ChooseMattressBanner';
import ModulesList from './elements/ModulesList';
import ProductFeatures from './elements/ProductFeatures';
import DeliverySection from './elements/DeliverySection';
import ATRInfo from './elements/ATRInfo';
import CollectYourRibble from './elements/CollectYourRibble';
import RibbleComposition from './elements/RibbleComposition';
import ProductsInInterior from './elements/ProductsInInterior';
import NewCollectionAnnounce from './elements/NewCollectionAnnounce';
import styles from './PageProduct.module.css';

import type { FC, HTMLAttributes, MutableRefObject, MouseEvent } from 'react';
import type { ReviewData } from '@Types/Review';
import type { PecomDeliveryParameters } from '@Components/PecomDelivery';
import type { TagType, LinearTagData } from '@Promo/elems/LinearTag';
import type { SelectedReviewsData } from '@Pages/PageProduct/elements/ReviewsSection';

const PanelOperator = lazy(() => import('@Components/Operator/PanelOperator'));

const PageProduct: FC<HTMLAttributes<HTMLDivElement>> = (props) => {
  const { className, ...restProps } = props;
  const { isMobileM, isOnlyMobile } = useMedias();
  const { isCSR } = useRenderType();
  const { totalHeight, contentHeight } = HeaderStore.useSizes();
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const [selectedReviews, setSelectedReviews] = useState<SelectedReviewsData>('all');
  const [nowPageReviews, setNowPageReviews] = useState(Number(searchParams.get('page') || 1));
  const meta = Meta.useMeta();
  const refCharacteristics = useRef<HTMLDivElement>();
  const refReviews = useRef<HTMLDivElement>();
  const refReviewsList = useRef<HTMLDivElement>();
  const refRelatedProducts = useRef<HTMLDivElement>();
  const refAtr = useRef<HTMLDivElement>();
  const featureFlags = useFeatureFlags();
  const page = PageProductStore.useData();
  const modules = PageProductStore.useModules();
  const schemeImage = page.schemes?.length > 0 ? page.schemes[0].image : null;
  const isAtr = page.isAtr;
  const isRibble = page.classifications?.includes('ribble');
  const findYourOwns = page.findYourOwn;
  const newCollectionAnnounce = page.newCollectionAnnounce;
  const vozvyshayaComfort = page.vozvyshayaComfort;
  const flowersBlock = page.flowersBlock;
  const isCollectionAnnounce = !newCollectionAnnounce && !vozvyshayaComfort && !flowersBlock;
  const isComingSoon = page.product.status === 'ComingSoon';
  const getSingleTag = (): TagType => {
    if (page.product.hasKitActivity) return 'kits';
    if (page.product.extraDiscountTag) return 'extra';

    return null;
  };

  const goodsInfo = useMemo<PecomDeliveryParameters[]>(() => {
    const productParameters = [];
    if (page.isModular) {
      modules.forEach((module) => {
        if (module.count > 0) {
          productParameters.push({
            id: module.id,
            quantity: module.count,
          });
        }
      });
    } else {
      productParameters.push({
        id: page.product.id,
        quantity: 1,
      });
    }
    return productParameters;
  }, [page.isModular, modules, page.product.id]);

  const hasMattressBanner = useMemo(() => {
    return (
      ['matrasy', 'krovati'].includes(page.categoryTranslite) && featureFlags.mattressQuizAvailable
    );
  }, [featureFlags.mattressQuizAvailable, page.categoryTranslite]);

  const needSeparator = !hasMattressBanner && !findYourOwns;

  const siteReviews = useMemo(() => {
    const all = (page.reviewsSubgallery || []).filter((review: ReviewData) => {
      return review.source === 'site';
    });
    const withPhoto = all.filter((review: ReviewData) => review.photos?.length > 0);

    return { all, withPhoto };
  }, [page]);

  const characteristicsTabs = useMemo(() => {
    const tabs = [];
    page.schemeImages?.tags.forEach((tag) => {
      tabs.push({
        ...tag,
        data: page.schemeImages.images.filter((image) => image.tagId === tag.id),
      });
    });

    return tabs;
  }, [page.schemeImages]);

  const scrollToRef = useCallback(
    (ref: MutableRefObject<HTMLDivElement>) => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      let top = window.scrollY + rect.top;

      if (isOnlyMobile && window.scrollY > rect.top) {
        top -= totalHeight;
      } else if (!isOnlyMobile) {
        top -= contentHeight;
      }

      window.scrollTo({ top, behavior: 'smooth' });
    },
    [totalHeight, contentHeight, isOnlyMobile],
  );

  const handleAddReview = useCallback(() => {
    ModalWindows.open('SendReview', { product: page.product });
  }, [page]);

  const handleClickCharacteristics = useCallback(() => {
    scrollToRef(refCharacteristics);
  }, [scrollToRef]);

  const handleClickReviews = useCallback(() => {
    scrollToRef(refReviews);
  }, [scrollToRef]);

  const handleClickAtr = useCallback(() => {
    scrollToRef(refAtr);
  }, [scrollToRef]);

  const handleChangePage = useCallback(
    (e: MouseEvent, pageNum: number) => {
      setNowPageReviews(pageNum);
      scrollToRef(refReviewsList);
    },
    [scrollToRef],
  );

  const handleChangeSelectedReviews = useCallback((e: MouseEvent, kind: SelectedReviewsData) => {
    setSelectedReviews(kind);
    setNowPageReviews(1);
  }, []);

  /** Opens send review modal when page is loaded and there is a url parameter */
  useEffect(() => {
    const hasReviewUrlParameter = searchParams.has('review');

    if (!hasReviewUrlParameter) return;

    const orderId = searchParams.get('review');

    ModalWindows.open('SendReview', { product: page.product, orderId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //TODO: Убрать обогащение объекта после переноса функционала с бэка в админку
  const extraDiscountData: LinearTagData = page.product?.extraDiscountTag
    ? { ...page.product?.extraDiscountTag, icon: 'fire' }
    : null;

  return (
    <div {...restProps} className={cn(styles.page, className)} data-testid='page-product'>
      <div
        className={cn(styles.mainContainer, styles.wrapperMain)}
        {...(page.product.isActive && {
          itemScope: true,
          itemType: 'http://schema.org/Product',
          itemRef: 'reviews-section',
        })}
      >
        <BreadcrumbsWrapper
          className={styles.breadcrumbs}
          breadcrumbs={page.breadcrumbs}
          breadcrumbsSeoData={page.breadcrumbsSeoData}
        />

        <Layout
          className={styles.mainContent}
          cnSidebar={styles.sidebar}
          sidebar={
            !isMobileM && (
              <Sidebar
                goodsInfo={goodsInfo}
                onClickCharacteristics={handleClickCharacteristics}
                onClickReviews={handleClickReviews}
                onClickAtr={handleClickAtr}
              />
            )
          }
        >
          <div>
            <PhotoGallery
              images={page.mediaGallery}
              tags={page.product.tags}
              singleTag={getSingleTag()}
              extraDiscountTag={extraDiscountData}
              productId={page.product.id}
              comingSoon={isComingSoon}
            />

            {isMobileM && (
              <div className={styles.wrapperSidebar}>
                <Sidebar
                  goodsInfo={goodsInfo}
                  onClickCharacteristics={handleClickCharacteristics}
                  onClickReviews={handleClickReviews}
                  onClickAtr={handleClickAtr}
                />
              </div>
            )}

            {isAtr && (
              <div ref={refAtr}>
                <ATRInfo className={styles.promoInfo} />
              </div>
            )}

            {newCollectionAnnounce && (
              <div>
                <NewCollectionAnnounce
                  className={styles.promoInfo}
                  atrInfo={newCollectionAnnounce}
                />
              </div>
            )}

            {vozvyshayaComfort && (
              <div>
                <NewCollectionAnnounce className={styles.promoInfo} atrInfo={vozvyshayaComfort} />
              </div>
            )}

            {flowersBlock && (
              <div>
                <NewCollectionAnnounce className={styles.promoInfo} atrInfo={flowersBlock} />
              </div>
            )}

            {page.cylindo && <LazyProductModel className={styles.cylindo} medias={page.cylindo} />}

            {schemeImage && (
              <div className={styles.layers}>
                <MattressesLayers
                  schemeImage={schemeImage}
                  layers={page.layers}
                  priorityParameter={page.priorityParameter}
                />
              </div>
            )}

            {page.description && isCollectionAnnounce && (
              <div className={styles.descriptionWrapper}>
                <div
                  className={styles.description}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    // TODO: это лучше вынести на бэкенд
                    // Вырезаем параграф с пробельным символом, иначе он сдвигает контент и визуально отступы между блоками становятся больше
                    __html: page.description.replace(/<p>&nbsp;<\/p>/, ''),
                  }}
                />
              </div>
            )}

            {modules?.length > 0 &&
              (isRibble ? (
                <RibbleComposition modules={modules} className={styles.modules} />
              ) : (
                <ModulesList modules={modules} className={styles.modules} />
              ))}

            {isRibble && (
              <CollectYourRibble
                className={styles.ribbleBanner}
                modelId={page.product.modelId}
                fabricId={page.product?.configurator?.fabricId}
                configurationId={page.product?.configurator?.configurationId}
              />
            )}

            <div
              className={styles.characteristics}
              ref={refCharacteristics}
              data-onboardid='characteristics'
              data-testid='characteristics'
            >
              <Characteristics title={t('characteristics')} tabs={characteristicsTabs} />
            </div>

            {page.features?.length > 0 && (
              <ProductFeatures className={styles.wrapperFeatures} features={page.features} />
            )}
          </div>
        </Layout>
      </div>

      {findYourOwns && (
        <ProductsInInterior
          img={findYourOwns.image}
          products={findYourOwns.products}
          title={findYourOwns.title}
          analyticsTitle={findYourOwns.category}
        />
      )}

      {hasMattressBanner && (
        <ChooseMattressBanner
          className={styles.mattressesBanner}
          title={t('mattresses.banner-title')}
          action={{
            title: t('mattresses.title'),
            link: '/promo/mattrasses',
          }}
        >
          {t('mattresses.description.1')}
          <br />
          {t('mattresses.description.2')}
        </ChooseMattressBanner>
      )}

      {needSeparator && <div className={styles.separator} />}

      <div
        className={cn(styles.wrapperAdditional, { [styles.hasSeparator]: needSeparator })}
        ref={refRelatedProducts}
      >
        {page.crossSalesProducts.products?.length > 0 && (
          <ProductsSection
            title={page.crossSalesProducts.title}
            tabs
            products={page.crossSalesProducts.products}
            analyticsTitle='Вам подойдет'
            data-testid='cross-sales-products'
          />
        )}

        {page.reviewsSubgallery.length > 0 ? (
          <>
            <div
              className={styles.sectionReviews}
              ref={refReviews}
              data-testid='feedback'
              id='reviews-section'
            >
              <ReviewsSection
                reviews={page.reviewsSubgallery}
                onAddReview={handleAddReview}
                onChangeSelectedReviews={handleChangeSelectedReviews}
              />
              <div className={styles.wrapperListReviews} ref={refReviewsList}>
                <ListReviews
                  className={styles.listReviews}
                  reviews={selectedReviews === 'all' ? siteReviews.all : siteReviews.withPhoto}
                  onChangePage={handleChangePage}
                  nowPage={nowPageReviews}
                />
              </div>
            </div>
            <div className={cn(styles.separator, styles.separatorAdditional)} />
          </>
        ) : (
          <div className={cn(styles.mainContainer, styles.emptyReviewWrapper)} ref={refReviews}>
            <EmptyReview onAddReview={handleAddReview} />
          </div>
        )}

        {page.instagram?.length > 0 && (
          <InstagramSection
            className={styles.sectionInstagram}
            title={t('titles.instagram')}
            posts={page.instagram}
            footnote={page.seoPreFooter}
            data-testid='house-furnishing'
          />
        )}

        {page.deliveryPage && (
          <ErrorBoundary fallback={null}>
            <div className={cn(styles.littleContainer, styles.sectionDelivery)}>
              <DeliverySection
                title={page.deliveryPage.title}
                delivery={page.deliveryPage}
                goodsInfo={goodsInfo}
                data-testid='delivery-assembling'
              />
            </div>
          </ErrorBoundary>
        )}

        <ComfortBuy className={styles.comfortBuy} />

        {page.sameProducts.products?.length > 0 && (
          <ProductsSection
            className={styles.sectionSimilar}
            title={page.sameProducts.title}
            products={page.sameProducts.products}
            data-testid='other-variants'
          />
        )}

        {page.upSalesProducts.products?.length > 0 && (
          <ProductsSection
            className={styles.sectionSimilar}
            title={page.upSalesProducts.title}
            tabs
            products={page.upSalesProducts.products}
            data-testid='similar-item'
          />
        )}

        {page.historyProducts.products?.length > 0 && (
          <ProductsSection
            className={styles.sectionHistory}
            cnSlide={styles.miniProduct}
            title={t('titles.recently-seen')}
            products={page.historyProducts.products}
            renderProduct={(product) => <NanoProductCard product={product} />}
            analyticsTitle='Вы недавно смотрели'
            data-testid='similar-item'
          />
        )}
      </div>

      {isCSR &&
        // TODO: meta.data.isOperatorTest - флаг для стресс-теста календаря доставок от 1С. Удалить по окончанию
        (meta.data.isOperatorTest ||
          (!isMobileM && meta.data.isOperator && meta.data.isOperatorPopupShow)) && (
          <ErrorBoundary>
            <Suspense fallback={null}>
              <PanelOperator
                className={cn(styles.operator, {
                  [styles.test]: !!meta.data.isOperatorTest,
                })}
                productId={page.product.id}
                region={meta.data.region}
              />
            </Suspense>
          </ErrorBoundary>
        )}
    </div>
  );
};

export default PageProduct;
